import type { ReactNode } from 'react'
import type { Joker, JokerEffect } from '../../engine/types'
import { JOKER_IMAGE } from '../../data/jokerImages'
import { JOKER_CATEGORY_COLORS, JOKER_CATEGORY_BG, JOKER_KEYWORD_RULES } from '../../data/jokerDisplayConfig'
import { cn } from '../utils/cn'

function highlightDescription(text: string): ReactNode[] {
  type Segment = { start: number; end: number; text: string; color: string }
  const segments: Segment[] = []

  for (const rule of JOKER_KEYWORD_RULES) {
    const re = new RegExp(rule.pattern.source, rule.pattern.flags)
    let m: RegExpExecArray | null
    while ((m = re.exec(text)) !== null) {
      const s = m.index
      const e = s + m[0].length
      // skip if overlapping with existing segment
      if (!segments.some(seg => s < seg.end && e > seg.start)) {
        segments.push({ start: s, end: e, text: m[0], color: rule.color })
      }
    }
  }

  segments.sort((a, b) => a.start - b.start)

  if (segments.length === 0) return [text]

  const result: ReactNode[] = []
  let cursor = 0
  segments.forEach((seg, i) => {
    if (cursor < seg.start) {
      result.push(text.slice(cursor, seg.start))
    }
    result.push(
      <span key={i} style={{ color: seg.color, fontWeight: 700 }}>{seg.text}</span>
    )
    cursor = seg.end
  })
  if (cursor < text.length) {
    result.push(text.slice(cursor))
  }
  return result
}

type JokerCardProps = {
  joker: Joker
  size?: 'sm' | 'md'
  isActive?: boolean
  effect?: JokerEffect
  onClick?: () => void
  showPrice?: boolean
  price?: number
  disabled?: boolean
}

export function JokerCard({
  joker,
  size = 'sm',
  isActive = false,
  effect,
  onClick,
  showPrice = false,
  price,
  disabled = false,
}: JokerCardProps) {
  const color = JOKER_CATEGORY_COLORS[joker.category] || JOKER_CATEGORY_COLORS.utility
  const bg = JOKER_CATEGORY_BG[joker.category] || JOKER_CATEGORY_BG.utility
  const w = size === 'sm' ? 120 : 160
  const h = size === 'sm' ? 160 : 240
  const imgSize = size === 'sm' ? 80 : 112

  return (
    <div
      className={cn(
        'relative group/joker',
        isActive && 'z-10',
      )}
      style={{
        width: w,
        height: h,
        animation: isActive ? 'joker-trigger 500ms ease-out' : undefined,
      }}
    >
      {/* Main card body */}
      <div
        className={cn(
          'relative w-full h-full flex flex-col overflow-hidden transition-all duration-200',
          !disabled && 'group-hover/joker:-translate-y-1',
          onClick && !disabled && 'cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed',
          isActive && 'scale-110',
        )}
        style={{
          borderRadius: 10,
          border: `2px solid ${color}60`,
          background: bg,
          boxShadow: [
            '0 2px 8px rgba(0,0,0,0.15)',
            'inset 0 1px 0 rgba(255,255,255,0.4)',
            `0 0 12px ${color}20`,
          ].join(', '),
        }}
        onClick={!disabled ? onClick : undefined}
      >
        {/* ── Vertical JOKER text — left side (贴顶，上→下) ── */}
        <div
          className="absolute top-0 flex flex-col items-center pointer-events-none"
          style={{ left: 2, width: 16, zIndex: 2, paddingTop: 4 }}
        >
          {'JOKER'.split('').map((ch, i) => (
            <span
              key={i}
              className="block text-center"
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 900,
                fontSize: size === 'sm' ? 15 : 19,
                lineHeight: 1.1,
                width: 16,
                color,
                userSelect: 'none',
              }}
            >
              {ch}
            </span>
          ))}
        </div>

        {/* ── Vertical JOKER text — right side (贴底，倒写) ── */}
        <div
          className="absolute bottom-0 flex flex-col items-center pointer-events-none"
          style={{ right: 2, width: 16, zIndex: 2, paddingBottom: 4, transform: 'rotate(180deg)' }}
        >
          {'JOKER'.split('').map((ch, i) => (
            <span
              key={i}
              className="block text-center"
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 900,
                fontSize: size === 'sm' ? 15 : 19,
                lineHeight: 1.1,
                width: 16,
                color,
                userSelect: 'none',
              }}
            >
              {ch}
            </span>
          ))}
        </div>

        {/* ── Image area (fills most of the card) ── */}
        <div
          className="relative flex-1 flex items-center justify-center"
        >
          {/* Subtle radial glow behind image */}
          <div
            className="absolute rounded-full"
            style={{
              width: imgSize + 20,
              height: imgSize + 20,
              background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
            }}
          />
          {JOKER_IMAGE[joker.id] ? (
            <img
              src={JOKER_IMAGE[joker.id]}
              alt={joker.name}
              className="relative object-contain"
              style={{
                width: imgSize,
                height: imgSize,
                filter: `drop-shadow(0 2px 6px rgba(0,0,0,0.3)) drop-shadow(0 0 8px ${color}30)`,
              }}
            />
          ) : (
            <div
              className="relative rounded-lg"
              style={{
                width: imgSize,
                height: imgSize,
                background: `${color}18`,
                border: `1px dashed ${color}40`,
              }}
            />
          )}
        </div>


        {/* ── Price badge (optional) ── */}
        {showPrice && price != null && (
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 rounded-b-md px-2 py-0.5 text-[10px] font-extrabold"
            style={{
              background: '#C9A96E',
              color: '#2A1800',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              zIndex: 5,
            }}
          >
            ${price}
          </div>
        )}

        {/* ── Hover glow ── */}
        <div
          className="absolute inset-0 rounded-[10px] opacity-0 group-hover/joker:opacity-100 transition-opacity duration-200 pointer-events-none"
          style={{
            boxShadow: `0 0 20px ${color}30, 0 0 40px ${color}10`,
          }}
        />
      </div>

      {/* ── Hover description tooltip ── */}
      <div
        className="absolute opacity-0 group-hover/joker:opacity-100 transition-opacity duration-200 pointer-events-none"
        style={{
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: 6,
          width: size === 'sm' ? 180 : 220,
          zIndex: 30,
        }}
      >
        <div
          className="rounded-lg px-3 py-2"
          style={{
            background: 'rgba(20,15,30,0.95)',
            border: `1px solid ${color}40`,
            boxShadow: `0 4px 16px rgba(0,0,0,0.5), 0 0 12px ${color}15`,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            className="text-[13px] font-bold mb-1"
            style={{ color }}
          >
            {joker.name}
          </div>
          <div
            className="text-[12px] leading-[1.5]"
            style={{ color: 'rgba(200,190,220,0.75)' }}
          >
            {highlightDescription(joker.description)}
          </div>
        </div>
      </div>

      {/* ── Effect popup (scoring animation) ── */}
      {effect && (
        <div
          className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded text-[11px] font-bold text-white"
          style={{
            background: effect.type === 'add_chips'
              ? 'var(--color-chips)'
              : effect.type === 'add_mult'
                ? 'var(--color-mult)'
                : 'var(--color-accent-gold)',
            animation: 'mult-pop 300ms ease-out',
            zIndex: 20,
          }}
        >
          {effect.type === 'mult_mult' ? 'x' : '+'}{effect.value}
        </div>
      )}
    </div>
  )
}
